import React, { useEffect, createContext, useContext, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import { AnimatePresence, motion } from "framer-motion";
import Cookies from "js-cookie";

// 페이지 컴포넌트 import
import Onboarding from "./components/Onboarding";
import SelectPage from "./pages/SelectPage";
import LawyerVerification from "./pages/LawyerVerification";
import ApplicationTypePage from "./pages/ApplicationTypePage";
import UserDataCollection from "./pages/UserDataCollection";
import ScreenshotSubmission from "./pages/ScreenshotSubmission";
import NewsProofSubmission from "./pages/NewsProofSubmission";
import LawsuitAmountSelection from "./pages/LawsuitAmountSelection";
import AgreementPage from "./pages/AgreementPage";
import ElectronicSignature from "./pages/ElectronicSignature";
import ElectronicSignature2 from "./pages/ElectronicSignature2";
import ElectronicSignature3 from "./pages/ElectronicSignature3";
import KakaoAuth from "./pages/KakaoAuth";
import PaymentPage from "./pages/PaymentPage";
import PaymentMethodsPage from "./pages/PaymentMethodsPage";
import PaymentCreditCardPage from "./pages/PaymentCreditCardPage";
import PaymentMobilePage from "./pages/PaymentMobilePage";
import PaymentMobileResultPage from "./pages/PaymentMobileResultPage";
import PaymentBankTransferPage from "./pages/PaymentBankTransferPage";
import PaymentVerificationPage from "./pages/PaymentVerificationPage";
import CompletePage from "./pages/CompletePage";
import CheckStatusPage from "./pages/CheckStatusPage";
import StatusPage from "./pages/StatusPage";
import PlaintPage from "./pages/PlaintPage";
import PlaintCheckPage from "./pages/PlaintCheckPage";
import ProtectedRoute from "./components/ProtectedRoute";
import CheckPasswordPage from "./pages/CheckPasswordPage";
import ExternalRedirectPage from "./pages/ExternalRedirectPage";
import ModusignAuthPage from "./pages/ModusignAuthPage";
import Footer from "./components/Footer";
import CoOwnerDataCollection from "./pages/CoOwnerDataCollection";
import ConsultButton from "./components/ConsultButton";
import SignaturePadPage from "./pages/SignaturePadPage";
import IdCardSubmitPage from "./pages/IdCardSubmitPage";
import IdCardCameraPage from "./pages/IdCardCameraPage";
import IdCardCropPage from "./pages/IdCardCropPage";
import IdentityVerificationInputPage from "./pages/IdentityVerificationInputPage";
import StatusIdCardSubmitPage from "./pages/StatusIdCardSubmitPage";
import StatusIdCardCameraPage from "./pages/StatusIdCardCameraPage";
import StatusIdCardCropPage from "./pages/StatusIdCardCropPage";
import StatusScreenshotSubmission from "./pages/StatusScreenshotSubmission";
import StatusSignaturePadPage from "./pages/StatusSignaturePadPage";
import AdditionalEvidenceLandingPage from "./pages/AdditionalEvidenceLandingPage";
import AdditionalEvidenceGuidePage from "./pages/AdditionalEvidenceGuidePage";
import AdditionalEvidencePage from "./pages/AdditionalEvidencePage";
import AdditionalEvidenceDonePage from "./pages/AdditionalEvidenceDonePage";
import AdditionalEvidenceCompletePage from "./pages/AdditionalEvidenceCompletePage";
import StatusAdditionalEvidencePage from "./pages/StatusAdditionalEvidencePage";

import "./App.css";

// 뷰포트 컴포넌트 import
const ViewportContext = createContext({});

// 방향 컨텍스트 생성
export const DirectionContext = createContext(0);

// 뷰포트 프로바이더 컴포넌트
const ViewportProvider = ({ children }) => {
  const isDesktop = useMediaQuery({ minWidth: 768 });

  return (
    <ViewportContext.Provider value={{ isDesktop }}>
      {children}
    </ViewportContext.Provider>
  );
};

// 뷰포트 컨텍스트 훅
const useViewport = () => {
  const context = useContext(ViewportContext);
  if (context === undefined) {
    throw new Error("useViewport must be used within a ViewportProvider");
  }
  return context;
};

// URL 파라미터 추출 및 저장을 위한 컴포넌트
const ReferralHandler = () => {
  const location = useLocation();

  useEffect(() => {
    // URL에서 ref와 refDetail 파라미터 추출
    const searchParams = new URLSearchParams(location.search);
    const ref = searchParams.get("ref");
    const refDetail = searchParams.get("refDetail");

    if (ref && refDetail) {
      // 추출한 값을 쿠키에 저장 (7일 유효기간)
      Cookies.set("referralSource", ref, { expires: 7, path: "/" });
      Cookies.set("referralDetail", refDetail, { expires: 7, path: "/" });
    }
  }, [location]);

  return null;
};

// 스크롤 상단 복원 컴포넌트
const ScrollToTop = () => {
  const { pathname } = useLocation();

  // 강력한 스크롤 초기화 함수
  const forceScrollTop = () => {
    // 모든 가능한 스크롤 위치를 0으로 설정
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // iOS/Safari 대응을 위한 추가 처리
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.scrollTo(0, 0);
      document.documentElement.scrollTo(0, 0);
    }

    // ContentWrapper 스크롤 초기화
    const contentWrapper = document.querySelector(".content-wrapper");
    if (contentWrapper) {
      contentWrapper.scrollTop = 0;
    }

    // 다른 가능한 스크롤 컨테이너도 초기화
    const containers = document.querySelectorAll(
      ".scrollable-container, .page-container",
    );
    containers.forEach((container) => {
      if (container) container.scrollTop = 0;
    });
  };

  useEffect(() => {
    try {
      // 강력한 스크롤 초기화 실행
      forceScrollTop();

      // 추가 스크롤 초기화 (지연 실행)
      setTimeout(() => {
        // 두 번째 강력한 스크롤 초기화 실행
        forceScrollTop();
      }, 100);
    } catch (error) {
      // 오류 무시
    }
  }, [pathname]);

  return null;
};

// 인앱 브라우저 감지 및 리다이렉트 로직
const BrowserRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 인앱 브라우저 감지
    if (isInAppBrowser() && location.pathname !== "/external-redirect") {
      const hasRedirected = sessionStorage.getItem("hasRedirected");

      if (!hasRedirected) {
        // 원래 URL 저장
        sessionStorage.setItem("originalUrl", window.location.href);

        // 리다이렉트 플래그 설정
        sessionStorage.setItem("hasRedirected", "true");

        // 외부 브라우저로 즉시 리다이렉트
        const currentUrl = window.location.href;

        // 카카오톡 인앱 브라우저에서는 특별한 URL 스키마 사용
        if (navigator.userAgent.indexOf("KAKAOTALK") > -1) {
          window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(
            currentUrl,
          )}`;
        }
        // 안드로이드 기기의 다른 인앱 브라우저
        else if (/android/i.test(navigator.userAgent)) {
          window.location.href = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;end`;
        }
        // iOS 기기의 다른 인앱 브라우저
        else if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          window.location.href = currentUrl;
        }
        // 그 외 플랫폼
        else {
          window.location.href = currentUrl;
        }

        // 리다이렉트 페이지로 이동 (리다이렉트가 실패했을 경우를 대비)
        setTimeout(() => {
          navigate("/external-redirect");
        }, 300);
      }
    }
  }, [location.pathname, navigate]);

  return null;
};

// 일반 라우터 (애니메이션 효과 제거)
const BasicRoutes = () => {
  const location = useLocation();

  return (
    <AppContainer>
      <ContentWrapper className="content-wrapper">
        <ScrollToTop />
        <Routes location={location}>
          {/* 외부 브라우저 리다이렉트 페이지 */}
          <Route path="/external-redirect" element={<ExternalRedirectPage />} />

          {/* 첫 페이지는 항상 Onboarding - 인증 불필요 */}
          <Route path="/" element={<Onboarding />} />

          {/* 인증 불필요 페이지 hghghg */}
          <Route path="/select" element={<SelectPage />} />
          <Route path="/application-type" element={<ApplicationTypePage />} />

          <Route
            path="/identity-verification-input"
            element={<IdentityVerificationInputPage />}
          />
          <Route path="/complete" element={<CompletePage />} />
          <Route path="/check-status" element={<CheckStatusPage />} />
          <Route path="/plaint" element={<PlaintPage />} />
          <Route path="/plaintCheck" element={<PlaintCheckPage />} />
          <Route path="/lawyer-verification" element={<LawyerVerification />} />
          <Route
            path="/additionalevidence"
            element={<AdditionalEvidenceLandingPage />}
          />
          <Route
            path="/additionalevidence/guide"
            element={<AdditionalEvidenceGuidePage />}
          />
          <Route
            path="/additionalevidence/auth"
            element={<AdditionalEvidencePage />}
          />
          <Route
            path="/additionalevidence/done"
            element={<AdditionalEvidenceDonePage />}
          />
          <Route
            path="/additionalevidence/complete"
            element={<AdditionalEvidenceCompletePage />}
          />

          {/* 인증 필요 페이지 */}
          <Route
            path="/user-data"
            element={
              <ProtectedRoute>
                <UserDataCollection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/co-owner-data"
            element={
              <ProtectedRoute>
                <CoOwnerDataCollection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/screenshot"
            element={
              <ProtectedRoute>
                <ScreenshotSubmission />
              </ProtectedRoute>
            }
          />
          <Route
            path="/news-proof"
            element={
              <ProtectedRoute>
                <NewsProofSubmission />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lawsuit-amount"
            element={
              <ProtectedRoute>
                <LawsuitAmountSelection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreement"
            element={
              <ProtectedRoute>
                <AgreementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/signature-pad"
            element={
              <ProtectedRoute>
                <SignaturePadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/id-card-submit"
            element={
              <ProtectedRoute>
                <IdCardSubmitPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/id-card-camera"
            element={
              <ProtectedRoute>
                <IdCardCameraPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/id-card-crop"
            element={
              <ProtectedRoute>
                <IdCardCropPage />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/kakao-auth"
            element={
              <ProtectedRoute>
                <KakaoAuth />
              </ProtectedRoute>
            }
          />
          <Route
            path="/modusign-auth"
            element={
              <ProtectedRoute>
                <ModusignAuthPage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/payment-methods"
            element={
              <ProtectedRoute>
                <PaymentMethodsPage />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/payment"
            element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            }
          /> */}
          {/* <Route
            path="/payment/creditcard"
            element={
              <ProtectedRoute>
                <PaymentCreditCardPage />
              </ProtectedRoute>
            }
          /> */}
          {/* <Route
            path="/payment/mobile"
            element={
              <ProtectedRoute>
                <PaymentMobilePage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/payment/mobile/result"
            element={
              <ProtectedRoute>
                <PaymentMobileResultPage />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/payment/banktransfer"
            element={
              <ProtectedRoute>
                <PaymentBankTransferPage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/payment-verification"
            element={
              <ProtectedRoute>
                <PaymentVerificationPage />
              </ProtectedRoute>
            }
          />
          <Route path="/status" element={<StatusPage />} />
          <Route
            path="/status/id-card/:applicationNumber"
            element={
              // <ProtectedRoute>
              <StatusIdCardSubmitPage />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/status/id-card-camera/:applicationNumber"
            element={
              // <ProtectedRoute>
              <StatusIdCardCameraPage />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/status/id-card-crop/:applicationNumber"
            element={
              // <ProtectedRoute>
              <StatusIdCardCropPage />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/status/screenshot/:applicationNumber"
            element={
              // <ProtectedRoute>
              <StatusScreenshotSubmission />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/status/signature/:applicationNumber"
            element={
              // <ProtectedRoute>
              <StatusSignaturePadPage />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/status/additional-evidence/:applicationNumber"
            element={<StatusAdditionalEvidencePage />}
          />
          <Route
            path="/check-password/:applicationNumber"
            element={<CheckPasswordPage />}
          />
          {/* 404 페이지 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        {/* <ConsultButton /> */}
      </ContentWrapper>
    </AppContainer>
  );
};

// 네비게이션 컨텍스트 제공자
const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const navigateTo = (path) => {
    navigate(path);
  };

  return (
    <DirectionContext.Provider value={{ navigateTo }}>
      {children}
    </DirectionContext.Provider>
  );
};

// 인앱 브라우저 감지 함수
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const uaLower = ua.toLowerCase();

  // 인스타그램은 Onboarding에서 모달로 처리하므로 제외
  if (ua.indexOf("Instagram") > -1 || uaLower.indexOf("instagram") > -1)
    return false;

  // 카카오톡 인앱 브라우저 감지
  if (ua.indexOf("KAKAOTALK") > -1 || uaLower.indexOf("kakaotalk") > -1)
    return true;

  // 네이버 인앱 브라우저 감지
  if (ua.indexOf("NAVER") > -1 && ua.indexOf("Mobile") > -1) return true;

  // 페이스북 인앱 브라우저 감지
  if (ua.indexOf("FBAN") > -1 || ua.indexOf("FBAV") > -1) return true;

  // 라인 인앱 브라우저 감지
  if (ua.indexOf("Line") > -1) return true;

  // 기타 인앱 브라우저 감지
  if (
    /(iPhone|iPod|iPad)(?!.*Safari)/i.test(ua) &&
    !/(Chrome|CriOS|FxiOS)/i.test(ua)
  )
    return true;
  if (/Android.*(wv|.0.0.0)/i.test(ua)) return true;

  return false;
};

// 레이아웃 스타일드 컴포넌트 추가
const AppLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// 앱 컨테이너 수정 - 스크롤 허용
const AppContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background-color: #000000; /* 검은색 배경으로 흰색 깜빡임 방지 */
  display: flex;
  flex-direction: column;
  overflow: visible; /* 오버플로우 숨김 처리에서 visible로 변경 */
`;

// 메인 콘텐츠 스타일링 - 스크롤 활성화
const ContentWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* 콘텐츠 래퍼에 스크롤 적용 */
  height: auto; /* 뷰포트 높이에서 자동 높이로 변경 */
  min-height: 100vh; /* 최소 높이 설정 */
  scroll-behavior: auto; /* 기본 스크롤 동작으로 변경 */
  -webkit-overflow-scrolling: touch; /* iOS 스크롤 부드럽게 */

  /* 불필요한 스크롤 최적화 속성 제거 */
  /* 스크롤바 제거 (필요한 경우) */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
`;

// 배경 레이어 - 항상 있는 배경 이미지
const BackgroundLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  z-index: 0;
`;

// 404 페이지 컴포넌트
const NotFoundPage = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });

  return (
    <PageBackground>
      <Container>
        <Overlay />
        <Content>
          <TextSection>
            <MainTitle>404</MainTitle>
            <SubTitle>페이지를 찾을 수 없습니다.</SubTitle>
          </TextSection>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 공통 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  @media (min-width: 769px) {
    padding: 20px;
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 768px) {
    border-radius: 20px;
    height: 700px;
    min-height: 700px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
    padding: 20px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1;

  @media (min-width: 769px) {
    border-radius: 20px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  z-index: 2;
  width: 100%;
  height: 100%;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 20px;
    min-height: 100vh;
    justify-content: space-between;
  }

  @media (min-width: 769px) {
    min-height: 700px;
    padding: 60px 20px;
    justify-content: space-around;
  }
`;

const TextSection = styled.div`
  margin-top: 120px;
  color: white;
  z-index: 2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (min-width: 769px) {
    margin-top: 80px;
  }
`;

const MainTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 15px;
  word-break: keep-all;
  color: white;
`;

const SubTitle = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  word-break: keep-all;
  opacity: 0.9;
  color: white;
`;

// Footer 조건부 렌더링을 위한 컴포넌트
const ConditionalFooter = () => {
  const location = useLocation();

  // PaymentPage, PaymentMethodsPage, PaymentCreditCardPage에서만 푸터 표시
  if (
    location.pathname === "/payment" ||
    location.pathname === "/payment-methods" ||
    location.pathname === "/payment/creditcard"
  ) {
    return <Footer />;
  }

  // 그 외 페이지에서는 푸터 표시하지 않음
  return null;
};

function App() {
  return (
    <Router>
      <ViewportProvider>
        <ScrollToTop />
        <ReferralHandler />
        <NavigationProvider>
          <BrowserRedirectHandler />
          <AppContainer>
            <BackgroundLayer />
            <ContentWrapper className="content-wrapper">
              <BasicRoutes />
            </ContentWrapper>
            <ConditionalFooter />
          </AppContainer>
        </NavigationProvider>
      </ViewportProvider>
    </Router>
  );
}

export default App;
